import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { cloneDeep } from 'lodash';

import { SVGIcon } from '@superops/super-icons';
import { Button, ButtonGroup } from '@superops/ui-design-system';

import {
  HourTypeItem,
  ProductTypeItem,
  BlockHourTypeItem,
  BlockMoneyTypeItem,
  ServiceBundleTypeItem
} from './ServiceItemTypes';
import {
  ServiceCatalogItem,
  ServiceTypeItem,
  ServiceTypeItemOfferingType
} from '../types';
import { ServiceCatalogFormContext } from '../ServiceCatalogForm';

type ServiceCatalogOptionsType = 'PRODUCT' | 'SERVICE' | 'PACKAGE';

interface ServiceItemOptionType {
  value: ServiceTypeItemOfferingType;
  label: string;
  icon: string;
}

type ServiceItemTypesType = {
  [key in 'SERVICE' | 'PACKAGE']: ServiceItemOptionType[];
};

const SERVICE_ITEM_TYPES: ServiceItemTypesType = {
  SERVICE: [
    { value: 'HOURS', label: 'admin.serviceCatalog.hour', icon: 'TimerNav' },
    { value: 'QUANTITY', label: 'common.quantity', icon: 'Cart' }
  ],
  PACKAGE: [
    {
      value: 'BLOCK_HOUR',
      label: 'admin.serviceCatalog.blockHour',
      icon: 'HourglasswireEnd'
    },
    {
      value: 'BLOCK_MONEY',
      label: 'admin.serviceCatalog.blockMoney',
      icon: 'Billable'
    },
    {
      value: 'SERVICE_BUNDLE',
      label: 'admin.serviceCatalog.serviceBundle',
      icon: 'Cube'
    }
  ]
};

function ServiceCatalogItemCostAndPrice({
  selectedCatologType,
  selectedCatalogItem,
  handlePropertyChange,
  handlePropertiesChange,
  disableServiceItemTypeSwitch,
  disableServiceCatalogItemCostAndPrice
}: {
  selectedCatologType: ServiceCatalogOptionsType;
  selectedCatalogItem: ServiceCatalogItem;
  handlePropertyChange: (key, value) => void;
  handlePropertiesChange: (ServiceCatalogItem: ServiceCatalogItem) => void;
  disableServiceItemTypeSwitch: boolean;
  disableServiceCatalogItemCostAndPrice: boolean;
}): React.ReactElement {
  const { resetErrors } = useContext(ServiceCatalogFormContext);

  const packageTypeServiceItems = ['BLOCK_HOUR', 'BLOCK_MONEY'];

  const serviceBundleTypeServiceItems = ['SERVICE_BUNDLE'];

  function handleBundledItems(selectedItem) {
    if (!selectedItem) {
      return;
    }
    const updatedServiceTypeItem = {
      ...selectedCatalogItem?.serviceTypeItem,
      bundledItems: [
        ...selectedCatalogItem?.serviceTypeItem?.bundledItems,
        selectedItem
      ]
    };
    if (
      packageTypeServiceItems.includes(
        selectedCatalogItem?.serviceTypeItem?.offeringType
      )
    ) {
      updatedServiceTypeItem.redeemableServiceBundle = {
        ...selectedCatalogItem?.serviceTypeItem?.redeemableServiceBundle,
        applicableType: 'INCLUDED_ITEMS'
      };
    }
    handlePropertyChange('serviceTypeItem', updatedServiceTypeItem);
  }

  function removeBundledItem(itemId) {
    const clonedBundledItems = cloneDeep(
      selectedCatalogItem?.serviceTypeItem?.bundledItems
    );
    handlePropertyChange('serviceTypeItem', {
      ...selectedCatalogItem?.serviceTypeItem,
      bundledItems: clonedBundledItems.filter((item) => item.itemId !== itemId)
    });
  }

  const ServiceCatalogItemCostAndPriceFormMap: Record<
    ServiceTypeItemOfferingType,
    ({
      handleBundledItems,
      removeBundledItem,
      selectedCatalogItem,
      handlePropertyChange
    }: {
      handleBundledItems?: (item) => void;
      removeBundledItem?: (itemId) => void;
      selectedCatalogItem?: ServiceCatalogItem;
      handlePropertyChange?: (key, val) => void;
    }) => React.ReactElement
  > = {
    HOURS: HourTypeItem,
    QUANTITY: ProductTypeItem,
    BLOCK_HOUR: BlockHourTypeItem,
    BLOCK_MONEY: BlockMoneyTypeItem,
    SERVICE_BUNDLE: ServiceBundleTypeItem
  };

  function changeServiceItemType(
    serviceItemOptionValue: ServiceTypeItemOfferingType
  ) {
    resetErrors();
    let updatedServiceTypeItem: ServiceTypeItem = {
      offeringType: serviceItemOptionValue
    };
    if (packageTypeServiceItems.includes(serviceItemOptionValue)) {
      updatedServiceTypeItem = {
        ...updatedServiceTypeItem,
        redeemableServiceBundle: {
          applicableHours: 'ALL_HOURS',
          applicableType: 'INCLUDED_ITEMS'
        },
        bundledItems: []
      };
    }
    if (serviceBundleTypeServiceItems.includes(serviceItemOptionValue)) {
      updatedServiceTypeItem = {
        ...updatedServiceTypeItem,
        bundledItems: []
      };
    }
    if (serviceItemOptionValue === 'HOURS') {
      updatedServiceTypeItem = {
        ...updatedServiceTypeItem,
        hourlyServiceTypeItem: {
          ...updatedServiceTypeItem?.hourlyServiceTypeItem,
          roundUpValue: 1
        }
      };
    }
    handlePropertiesChange({
      ...selectedCatalogItem,
      sellingPrice: null,
      costPrice: null,
      serviceTypeItem: updatedServiceTypeItem
    });
  }

  const ServiceCatalogItemCostAndPriceForm =
    ServiceCatalogItemCostAndPriceFormMap[
      selectedCatalogItem?.serviceTypeItem?.offeringType
    ] || ProductTypeItem;

  return (
    <>
      {SERVICE_ITEM_TYPES[selectedCatologType] && (
        <ButtonGroup segmented spacing="extraTight">
          {SERVICE_ITEM_TYPES[selectedCatologType]?.map(
            (serviceItemOption: ServiceItemOptionType) => {
              const selected =
                selectedCatalogItem?.serviceTypeItem?.offeringType ===
                serviceItemOption?.value;
              return (
                <Button
                  onClick={() =>
                    changeServiceItemType(serviceItemOption?.value)
                  }
                  icon={<SVGIcon name={serviceItemOption?.icon} size="sm" />}
                  key={serviceItemOption?.value}
                  selected={selected}
                  outline={selected}
                  disabled={disableServiceItemTypeSwitch}
                >
                  {<FormattedMessage id={serviceItemOption?.label} />}
                </Button>
              );
            }
          )}
        </ButtonGroup>
      )}
      <ServiceCatalogItemCostAndPriceForm
        selectedCatalogItem={selectedCatalogItem}
        handlePropertyChange={handlePropertyChange}
        removeBundledItem={removeBundledItem}
        handleBundledItems={handleBundledItems}
        disableServiceCatalogItemCostAndPrice={
          disableServiceCatalogItemCostAndPrice
        }
      />
    </>
  );
}

export default ServiceCatalogItemCostAndPrice;
